import {useState, useMemo, useCallback} from 'react'
import type {CLIAgentMode} from '../components/task-view/CLIModePicker'
import {isSessionModeChangedMessage, isSessionResumeMessage} from '../types/session-events'
import {useLogsQuery} from './useLogs'
import {useSessionEventsQuery} from './use-session-events'
import {useEventsSchemaEnabled} from './use-events-schema-enabled'

const CLI_AGENT_MODES: readonly string[] = ['interactive', 'plan', 'autopilot']

function isValidCLIAgentMode(mode: string): mode is CLIAgentMode {
  return CLI_AGENT_MODES.includes(mode)
}

interface UseCLIModeSyncOptions {
  sessionId: string
  taskId?: string
}

interface UseCLIModeSyncResult {
  /** The mode that the dropdown should display */
  agentMode: CLIAgentMode
  /** Whether a mode change is in-flight. The picker stays enabled so users can queue another change. */
  isPending: boolean
  /** Call when the user picks a new mode from the dropdown. Returns the previous mode for snapshot rollback. */
  handleModeChange: (mode: CLIAgentMode) => {previousMode: CLIAgentMode}
  /** Call when the mode_switch command fails to roll back the optimistic update */
  handleModeChangeError: (previousMode: CLIAgentMode) => void
}

/**
 * Manages CLI agent mode state by deriving it from the log/events stream.
 */
export function useCLIModeSync({sessionId, taskId}: UseCLIModeSyncOptions): UseCLIModeSyncResult {
  const useEventsPath = useEventsSchemaEnabled() && !!taskId

  const {data: logs} = useLogsQuery({sessionId, enabled: !useEventsPath})
  const {data: eventsData} = useSessionEventsQuery({taskId: taskId ?? '', enabled: useEventsPath})
  const reconciliationSource = useEventsPath ? eventsData?.events : logs

  const derivedMode = useMemo<CLIAgentMode>(() => {
    if (!reconciliationSource || reconciliationSource.length === 0) return 'interactive'
    for (let i = reconciliationSource.length - 1; i >= 0; i--) {
      const entry = reconciliationSource[i]
      if (entry && isSessionModeChangedMessage(entry) && isValidCLIAgentMode(entry.data.newMode)) {
        return entry.data.newMode
      }

      // session.resume means the CLI restarted — it always starts in interactive mode
      if (entry && isSessionResumeMessage(entry)) {
        return 'interactive'
      }
    }

    return 'interactive'
  }, [reconciliationSource])

  const [pendingMode, setPendingMode] = useState<CLIAgentMode | null>(null)

  // Clear pendingMode when server confirms the mode change.
  // Uses the React "adjust state during render" pattern with useState
  // so prevDerivedMode updates synchronously on render restart.
  const [prevDerivedMode, setPrevDerivedMode] = useState(derivedMode)
  if (prevDerivedMode !== derivedMode) {
    setPrevDerivedMode(derivedMode)
    setPendingMode(null)
  }

  const agentMode = pendingMode ?? derivedMode

  const handleModeChange = useCallback(
    (mode: CLIAgentMode) => {
      const previousMode = agentMode
      setPendingMode(mode)
      return {previousMode}
    },
    [agentMode],
  )

  const handleModeChangeError = useCallback((_previousMode: CLIAgentMode) => {
    setPendingMode(null)
  }, [])

  return {agentMode, isPending: pendingMode !== null, handleModeChange, handleModeChangeError}
}
